import "./index.css";
import { Tooltip, message } from "antd";
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import ICON_VOICE from "../../assets/voice.svg";
import { getIframesToken, IS_IFRAMES } from "../../constants";

/**
 * @name VoiceRef 组件暴露给父组件的方法
 * @description 通过 ref 可以调用的方法
 */
export interface VoiceRef {
  /** 停止录音 */
  stopRecording: () => void;
}

/**
 * @name VoiceStatus 录音状态枚举
 * @description 定义组件的三种状态：空闲、录音中、上传中
 */
enum VoiceStatus {
  /** 空闲状态，可以开始录音 */
  IDLE = "idle",
  /** 正在录音 */
  RECORDING = "recording",
  /** 正在上传并转换 */
  UPLOADING = "uploading",
}

/**
 * @name InputMode 输入模式枚举
 * @description 定义组件的输入模式状态
 */
enum InputMode {
  /** 麦克风录音模式（可用） */
  MICROPHONE = "microphone",
  /** 麦克风不可用 */
  NO_MICROPHONE = "no_microphone",
  /** 未知（正在检测中） */
  UNKNOWN = "unknown",
}

/**
 * @name Voice2TextResponse API 响应类型
 * @description voice2text 接口的响应数据结构
 */
interface Voice2TextResponse {
  /** 是否成功 */
  success?: boolean;
  /** 转录后的文本内容 */
  text?: string;
  /** 错误信息 */
  error?: string;
  /** 错误码 */
  code?: number;
  /** 其他可能的响应字段 */
  [key: string]: unknown;
}

/**
 * @name VoiceProps 组件 Props 类型
 * @description Voice 组件的属性接口定义
 */
interface VoiceProps {
  /**
   * @name onTranscribed 转录完成回调
   * @param text 转录后的文本
   * @description 当语音成功转换为文本后调用此回调
   */
  onTranscribed?: (text: string) => void;
  /**
   * @name onError 错误处理回调
   * @param error 错误信息
   * @description 当发生错误时调用此回调
   */
  onError?: (error: Error) => void;
  /**
   * @name onStatusChange 状态变化回调
   * @param status 当前状态
   * @description 当录音状态发生变化时调用
   */
  onStatusChange?: (status: VoiceStatus) => void;
  /**
   * @name onAudioData 音频数据回调
   * @param data 频率数据数组 (0-255)
   * @description 实时返回音频频率数据用于可视化
   */
  onAudioData?: (data: Uint8Array) => void;
  /**
   * @name onRecordingChange 录音状态变化回调
   * @param isRecording 是否正在录音
   * @description 当录音开始或停止时调用
   */
  onRecordingChange?: (isRecording: boolean) => void;
  /**
   * @name disabled 是否禁用
   * @description 禁用后无法进行录音操作
   */
  disabled?: boolean;
}

/**
 * @name MAX_FILE_SIZE 最大音频文件大小
 * @description 限制录音文件大小为 10MB
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * @name MAX_RECORDING_DURATION 最大录音时长
 * @description 限制录音时长为 60 秒，超过自动停止录音
 */
const MAX_RECORDING_DURATION = 60 * 1000; // 60 秒

/**
 * @name getAuthToken 获取认证 Token
 * @description 根据当前环境（iframe 或普通模式）获取正确的认证 Token
 * @returns 认证 Token 字符串，如果不存在返回 null
 */
function getAuthToken(): string | null {
  if (IS_IFRAMES) {
    return localStorage.getItem(getIframesToken());
  }
  return sessionStorage.getItem("ZeitroA");
}

/**
 * @name checkMicrophoneAvailability 检测麦克风是否可用
 * @description 枚举音频输入设备，判断是否有可用的麦克风
 * @returns Promise<boolean> 是否有可用的麦克风
 */
async function checkMicrophoneAvailability(): Promise<boolean> {
  try {
    // 检查浏览器是否支持 mediaDevices API
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn("[Voice] mediaDevices API not supported");
      return false;
    }

    // 枚举所有媒体设备
    const devices = await navigator.mediaDevices.enumerateDevices();

    // 查找音频输入设备（麦克风）
    const audioInputDevices = devices.filter(
      (device) => device.kind === "audioinput"
    );

    // 检查是否有可用的麦克风
    // 注意：在未授权情况下，设备列表可能为空或只有空 label
    const hasMicrophone = audioInputDevices.length > 0;

    console.log(
      `[Voice] Detected ${audioInputDevices.length} audio input device(s)`,
      audioInputDevices
    );

    return hasMicrophone;
  } catch (error) {
    console.error("[Voice] Failed to detect microphone:", error);
    return false;
  }
}

/**
 * @name uploadVoiceFile 上传语音文件到 voice2text 接口
 * @param file 要上传的音频文件
 * @param signal 可选的 AbortSignal 用于取消请求
 * @returns Promise<Voice2TextResponse> 接口响应
 * @throws Error 当网络错误、认证失败或服务器错误时抛出异常
 */
async function uploadVoiceFile(
  file: File,
  signal?: AbortSignal
): Promise<Voice2TextResponse> {
  // 获取认证 Token
  const token = getAuthToken();
  if (!token) {
    throw new Error("Please log in first to use the voice feature");
  }

  // 构建 FormData
  const formData = new FormData();
  formData.append("file", file);

  // 发送请求（支持取消）
  const response = await fetch("/api/v1/system/voice2text", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      // 注意：使用 FormData 时不要手动设置 Content-Type
      // 浏览器会自动设置正确的 Content-Type 和 boundary
    },
    body: formData,
    signal,
  });

  // 处理 HTTP 错误状态码
  if (!response.ok) {
    // 尝试解析错误响应
    let errorMessage = `Request failed with status code: ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // 无法解析 JSON，使用默认错误信息
      if (response.status === 401) {
        errorMessage = "Authentication failed, please log in again";
      } else if (response.status === 413) {
        errorMessage = "File too large, please record a shorter audio";
      } else if (response.status === 415) {
        errorMessage = "Unsupported audio format";
      } else if (response.status >= 500) {
        errorMessage = "Server error, please try again later";
      }
    }
    throw new Error(errorMessage);
  }

  // 解析响应 JSON
  const data: Voice2TextResponse = await response.json();
  return data;
}

/**
 * @name Voice 语音输入组件
 * @description 负责录制用户语音并通过 API 转换为文本内容
 *
 * 功能流程：
 * 1. 组件挂载时检测麦克风是否可用
 * 2. 如果有麦克风：点击图标开始录音，再次点击停止录音
 * 3. 如果无麦克风：显示错误提示，禁止录音
 * 4. 录音最长 60 秒，超过自动停止
 * 5. 自动将录制的音频上传到 voice2text 接口
 * 6. 接口返回转录文本后，通过 onTranscribed 回调通知父组件
 *
 * 状态说明：
 * - IDLE: 空闲状态，显示麦克风图标
 * - RECORDING: 录音中，显示红色闪烁动画
 * - UPLOADING: 上传中，显示加载动画
 */
const Voice = forwardRef<VoiceRef, VoiceProps>(({
  onTranscribed,
  onError,
  onStatusChange,
  onAudioData,
  onRecordingChange,
  disabled = false,
}, ref) => {
  // 音频数据块存储
  const chunks = useRef<Array<Blob>>([]);
  // 媒体流引用
  const stream = useRef<MediaStream | null>(null);
  // 录音器引用
  const recorder = useRef<MediaRecorder | null>(null);
  // 录音使用的 MIME 类型（用于正确创建 Blob）
  const mimeTypeRef = useRef<string>("audio/webm");
  // 请求取消控制器
  const abortControllerRef = useRef<AbortController | null>(null);
  // 录音超时定时器（用于自动停止录音）
  const recordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 音频分析相关引用
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  // 当前状态
  const [status, setStatus] = useState<VoiceStatus>(VoiceStatus.IDLE);
  // 输入模式（麦克风可用/不可用）
  const [inputMode, setInputMode] = useState<InputMode>(InputMode.UNKNOWN);

  /**
   * @name 组件挂载时检测麦克风可用性
   */
  useEffect(() => {
    let isMounted = true;

    const detectMicrophone = async () => {
      const hasMicrophone = await checkMicrophoneAvailability();

      if (isMounted) {
        if (hasMicrophone) {
          setInputMode(InputMode.MICROPHONE);
          console.log("[Voice] Using microphone recording mode");
        } else {
          setInputMode(InputMode.NO_MICROPHONE);
          console.warn("[Voice] No microphone detected");
        }
      }
    };

    detectMicrophone();

    // 监听设备变化（用户插入/拔出麦克风）
    const handleDeviceChange = () => {
      detectMicrophone();
    };

    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        handleDeviceChange
      );
    }

    return () => {
      isMounted = false;

      // 停止录音器（阻止回调触发状态更新）
      if (recorder.current && recorder.current.state !== "inactive") {
        recorder.current.onstop = null;
        recorder.current.onerror = null;
        recorder.current.ondataavailable = null;
        try {
          recorder.current.stop();
        } catch {
          // 忽略停止时的错误
        }
        recorder.current = null;
      }

      // 释放媒体流（停止麦克风占用）
      if (stream.current) {
        stream.current.getTracks().forEach((track) => track.stop());
        stream.current = null;
      }

      // 取消进行中的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // 清除录音超时定时器
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      // 清空数据块
      chunks.current = [];

      // 移除设备变化监听
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener(
          "devicechange",
          handleDeviceChange
        );
      }
    };
  }, []);

  /**
   * @name 监听页面可见性变化（处理移动端后台切换）
   * @description 当用户切换到其他 App 或接电话时，自动停止录音
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      // 页面进入后台且正在录音时，自动停止录音
      if (document.hidden && status === VoiceStatus.RECORDING) {
        console.log("[Voice] Page hidden, stopping recording");
        message.warning("Recording stopped (app went to background)");

        // 清除录音超时定时器
        if (recordingTimerRef.current) {
          clearTimeout(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        // 停止录音器
        if (recorder.current && recorder.current.state !== "inactive") {
          recorder.current.onstop = null; // 阻止触发上传
          try {
            recorder.current.stop();
          } catch {
            // 忽略错误
          }
        }

        // 释放媒体流
        if (stream.current) {
          stream.current.getTracks().forEach((track) => track.stop());
          stream.current = null;
        }

        // 清理数据
        chunks.current = [];
        recorder.current = null;
        setStatus(VoiceStatus.IDLE);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [status]);

  /**
   * @name updateStatus 更新状态并触发回调
   * @param newStatus 新的状态
   */
  const updateStatus = useCallback(
    (newStatus: VoiceStatus) => {
      setStatus(newStatus);
      onStatusChange?.(newStatus);
    },
    [onStatusChange]
  );

  /**
   * @name handleError 统一错误处理
   * @param error 错误对象
   * @param userMessage 用户友好的错误消息
   */
  const handleError = useCallback(
    (error: unknown, userMessage: string) => {
      console.error("[Voice Component Error]", error);
      const errorObj =
        error instanceof Error ? error : new Error(String(error));

      // 显示用户友好的错误提示
      message.error(userMessage);

      // 调用错误回调
      onError?.(errorObj);

      // 重置状态
      updateStatus(VoiceStatus.IDLE);
    },
    [onError, updateStatus]
  );

  /**
   * @name clearRecordingTimer 清除录音超时定时器
   * @description 清除用于自动停止录音的定时器，防止内存泄漏
   */
  const clearRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  /**
   * @name stopAudioAnalysis 停止音频分析
   * @description 停止音频可视化的动画循环和释放音频上下文
   */
  const stopAudioAnalysis = useCallback(() => {
    // 停止动画循环
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // 断开音频源节点
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        // 忽略断开连接时的错误
      }
      sourceRef.current = null;
    }

    // 断开分析器节点
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch {
        // 忽略断开连接时的错误
      }
      analyserRef.current = null;
    }

    // 关闭音频上下文
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {
        // 忽略关闭时的错误
      }
      audioContextRef.current = null;
    }

    // 清空数据数组
    dataArrayRef.current = null;

    // 通知录音状态变化
    onRecordingChange?.(false);
  }, [onRecordingChange]);

  /**
   * @name startAudioAnalysis 开始音频分析
   * @param mediaStream 媒体流
   * @description 初始化 Web Audio API 进行实时音频分析
   */
  const startAudioAnalysis = useCallback((mediaStream: MediaStream) => {
    try {
      // 创建音频上下文
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) {
        console.warn("[Voice] Web Audio API is not supported in this browser.");
        return;
      }
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      // 创建分析器节点
      const analyser = audioContext.createAnalyser();
      // FFT 大小决定频率数据的精度，128 提供 64 个频率数据点
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;

      // 创建媒体流音频源
      const source = audioContext.createMediaStreamSource(mediaStream);
      sourceRef.current = source;

      // 连接音频源到分析器
      source.connect(analyser);

      // 创建数据数组用于存储频率数据
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;

      // 通知录音状态变化
      onRecordingChange?.(true);

      // 开始动画循环获取音频数据
      const updateAudioData = () => {
        if (!analyserRef.current || !dataArrayRef.current) {
          return;
        }

        // 获取频率数据
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        // 回调音频数据给父组件
        onAudioData?.(dataArrayRef.current);

        // 继续下一帧
        animationFrameRef.current = requestAnimationFrame(updateAudioData);
      };

      // 启动动画循环
      animationFrameRef.current = requestAnimationFrame(updateAudioData);

      console.log("[Voice] Audio analysis started with", bufferLength, "frequency bins");
    } catch (error) {
      console.error("[Voice] Failed to start audio analysis:", error);
    }
  }, [onAudioData, onRecordingChange]);

  /**
   * @name cleanupRecording 清理录音资源
   * @description 停止所有音轨，释放媒体流和录音器资源
   */
  const cleanupRecording = useCallback(() => {
    // 清除录音超时定时器
    clearRecordingTimer();

    // 停止音频分析
    stopAudioAnalysis();

    // 停止所有音轨
    if (stream.current) {
      stream.current.getTracks().forEach((track) => {
        track.stop();
      });
      stream.current = null;
    }
    // 清理录音器
    if (recorder.current) {
      recorder.current = null;
    }
    // 清空数据块
    chunks.current = [];
  }, [clearRecordingTimer, stopAudioAnalysis]);

  /**
   * @name processApiResponse 处理 API 响应
   * @param response API 响应数据
   * @description 统一处理上传后的 API 响应
   */
  const processApiResponse = useCallback(
    (response: Voice2TextResponse) => {
      if (response.text) {
        // 转录成功，直接回调给父组件
        onTranscribed?.(response.text);
      } else if (response.error) {
        // 服务器返回错误
        throw new Error(response.error);
      } else {
        // 响应格式异常，尝试从其他字段提取文本
        console.warn("[Voice] Unexpected response format:", response);
        const possibleTextFields = ["content", "result", "data", "transcript"];
        let foundText: string | null = null;
        for (const field of possibleTextFields) {
          if (typeof response[field] === "string" && response[field]) {
            foundText = response[field] as string;
            break;
          }
        }
        if (foundText) {
          // 转录成功，直接回调给父组件
          onTranscribed?.(foundText);
        } else {
          throw new Error("Unexpected response data format from server");
        }
      }
    },
    [onTranscribed]
  );

  /**
   * @name MIN_AUDIO_SIZE 最小有效音频大小
   * @description 音频文件必须大于此大小才被认为是有效的录音（约100字节头信息 + 一些数据）
   */
  const MIN_AUDIO_SIZE = 1000; // 1KB

  /**
   * @name processAndUploadAudio 处理并上传录制的音频
   * @description 将录制的音频块合并成文件并上传到服务器
   */
  const processAndUploadAudio = useCallback(async () => {
    // 检查是否有录制的数据
    if (chunks.current.length === 0) {
      // 没有音频数据，静默清理并重置状态，不请求接口
      console.warn("[Voice] No audio data recorded, skipping upload");
      cleanupRecording();
      updateStatus(VoiceStatus.IDLE);
      return;
    }

    // 获取实际使用的 MIME 类型和对应的文件扩展名
    const actualMimeType = mimeTypeRef.current;
    const fileExtension = actualMimeType.includes("mp4") ? "mp4" : "webm";

    // 合并所有音频块为一个 Blob（使用实际的 MIME 类型）
    const audioBlob = new Blob(chunks.current, { type: actualMimeType });

    // 检查文件是否有效（为空或太小）
    if (audioBlob.size === 0) {
      // 音频为空，静默清理并重置状态，不请求接口
      console.warn("[Voice] Audio blob is empty, skipping upload");
      cleanupRecording();
      updateStatus(VoiceStatus.IDLE);
      return;
    }

    // 检查音频是否太短（可能用户只是误触）
    if (audioBlob.size < MIN_AUDIO_SIZE) {
      // 音频太短，静默清理并重置状态，不请求接口
      console.warn("[Voice] Audio too short (size:", audioBlob.size, "bytes), skipping upload");
      cleanupRecording();
      updateStatus(VoiceStatus.IDLE);
      return;
    }

    // 更新状态为上传中
    updateStatus(VoiceStatus.UPLOADING);

    try {
      // 检查文件大小是否超过上限
      if (audioBlob.size > MAX_FILE_SIZE) {
        throw new Error("Recording file too large, please record a shorter audio (max 10MB)");
      }

      // 创建 File 对象（使用正确的扩展名和 MIME 类型）
      const timestamp = Date.now();
      const audioFile = new File([audioBlob], `voice_${timestamp}.${fileExtension}`, {
        type: actualMimeType,
        lastModified: timestamp,
      });

      // 创建新的 AbortController 用于取消请求
      abortControllerRef.current = new AbortController();

      // 调用上传函数（传入 signal 以支持取消）
      const response = await uploadVoiceFile(audioFile, abortControllerRef.current.signal);
      console.log(`response: `, response);
      processApiResponse(response);
    } catch (error) {
      // 如果是用户取消，不显示错误
      if (error instanceof Error && error.name === "AbortError") {
        console.log("[Voice] Upload cancelled");
        return;
      }
      const errorMessage =
        error instanceof Error ? error.message : "Voice to text conversion failed, please try again";
      handleError(error, errorMessage);
    } finally {
      cleanupRecording();
      updateStatus(VoiceStatus.IDLE);
    }
  }, [handleError, cleanupRecording, updateStatus, processApiResponse]);

  /**
   * @name startRecording 开始录音
   * @description 请求麦克风权限并开始录制音频
   */
  const startRecording = useCallback(async () => {
    try {
      // 检查 MediaRecorder API 是否可用（iOS Safari 14.3 以下不支持）
      if (typeof MediaRecorder === "undefined") {
        message.error("Your browser does not support voice recording");
        setInputMode(InputMode.NO_MICROPHONE);
        return;
      }

      // 清空之前的数据
      chunks.current = [];

      // 请求麦克风权限（使用 ideal 约束以提高设备兼容性）
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          sampleRate: { ideal: 16000 },
        },
      });

      // 如果成功获取到流，说明肯定有麦克风且已授权，修正状态
      setInputMode(InputMode.MICROPHONE);

      // 检查浏览器支持的 MIME 类型
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : MediaRecorder.isTypeSupported("audio/mp4")
            ? "audio/mp4"
            : "";

      // 保存实际使用的 MIME 类型，用于后续创建 Blob
      mimeTypeRef.current = mimeType || "audio/webm";

      // 创建 MediaRecorder
      const recorderOptions: MediaRecorderOptions = {
        audioBitsPerSecond: 128000,
      };
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }

      recorder.current = new MediaRecorder(stream.current, recorderOptions);

      // Update the actual used MIME type if available
      if (recorder.current.mimeType) {
        mimeTypeRef.current = recorder.current.mimeType;
      }

      // 监听数据可用事件
      recorder.current.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      // 监听录音停止事件
      recorder.current.onstop = () => {
        processAndUploadAudio();
      };

      // 监听录音错误事件
      recorder.current.onerror = (event: Event) => {
        console.error("[Voice] MediaRecorder error:", event);
        handleError(event, "Recording error, please try again");
      };

      // 开始录音
      recorder.current.start(1000);

      // 启动音频分析（用于声波可视化）
      if (stream.current) {
        startAudioAnalysis(stream.current);
      }

      // 更新状态
      updateStatus(VoiceStatus.RECORDING);

      // 设置录音超时定时器（60秒后自动停止）
      recordingTimerRef.current = setTimeout(() => {
        console.log("[Voice] Recording timeout, auto stopping after 60 seconds");
        message.info("Recording has reached the maximum duration of 60 seconds");

        // 自动停止录音
        if (recorder.current && recorder.current.state !== "inactive") {
          try {
            recorder.current.stop();
          } catch (stopError) {
            console.error("[Voice] Error auto-stopping recorder:", stopError);
          }
        }
      }, MAX_RECORDING_DURATION);
    } catch (error) {
      // 处理各种可能的错误
      let userMessage = "Unable to start recording";
      let shouldMarkNoMicrophone = false;

      if (error instanceof DOMException) {
        switch (error.name) {
          case "NotAllowedError":
          case "PermissionDeniedError":
            userMessage = "Microphone permission denied. Please allow microphone access in your browser settings.";
            shouldMarkNoMicrophone = true;
            break;
          case "NotFoundError":
          case "DevicesNotFoundError":
            userMessage = "No microphone detected. Please connect a microphone and try again.";
            shouldMarkNoMicrophone = true;
            break;
          case "NotReadableError":
          case "TrackStartError":
            userMessage = "Microphone is in use by another application. Please close other apps using the microphone.";
            break;
          case "OverconstrainedError":
            userMessage = "Microphone does not support current configuration.";
            break;
          case "SecurityError":
            userMessage = "Security restriction. Please use HTTPS to enable voice recording.";
            shouldMarkNoMicrophone = true;
            break;
          default:
            userMessage = `Recording failed: ${error.message}`;
        }
      }

      // 显示错误消息
      message.error(userMessage);

      // 如果是麦克风不可用的错误，更新状态
      if (shouldMarkNoMicrophone) {
        setInputMode(InputMode.NO_MICROPHONE);
      }

      cleanupRecording();
    }
  }, [
    processAndUploadAudio,
    handleError,
    cleanupRecording,
    updateStatus,
    startAudioAnalysis,
  ]);

  /**
   * @name stopRecording 停止录音
   * @description 停止当前的录音会话
   */
  const stopRecording = useCallback(() => {
    // 先清除录音超时定时器，防止重复触发
    clearRecordingTimer();
    stopAudioAnalysis();

    if (recorder.current && recorder.current.state !== "inactive") {
      try {
        recorder.current.stop();
      } catch (error) {
        console.error("[Voice] Error stopping recorder:", error);
        handleError(error, "Failed to stop recording");
        cleanupRecording();
        updateStatus(VoiceStatus.IDLE);
      }
    }
  }, [clearRecordingTimer, handleError, cleanupRecording, updateStatus]);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    stopRecording,
  }), [stopRecording]);

  /**
   * @name onRecording 用户点击录音按钮
   * @description 根据当前状态和输入模式执行相应操作
   */
  const onRecording = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();

      // 如果禁用则不响应
      if (disabled) {
        return;
      }

      // 如果正在上传，不响应点击
      if (status === VoiceStatus.UPLOADING) {
        message.info("Processing, please wait...");
        return;
      }

      // 如果还在检测中，提示用户稍等
      if (inputMode === InputMode.UNKNOWN) {
        message.info("Detecting device, please wait...");
        return;
      }

      // 即使检测显示没有麦克风，也允许尝试录音
      // 因为在某些浏览器中（如 Firefox/Safari），未授权时 enumerateDevices 可能返回空
      // 此时只有调用 getUserMedia (在 startRecording 中) 才能触发权限弹窗

      // 麦克风模式：开始录音
      if (status === VoiceStatus.IDLE) {
        startRecording();
      }
    },
    [disabled, status, inputMode, startRecording]
  );

  /**
   * @name getTooltipTitle 获取 Tooltip 提示文字
   * @returns 根据当前状态和模式返回不同的提示文字
   */
  const getTooltipTitle = useCallback(() => {
    if (inputMode === InputMode.UNKNOWN) {
      return "Detecting device...";
    }

    if (inputMode === InputMode.NO_MICROPHONE) {
      return "No microphone detected (Click to retry)";
    }

    switch (status) {
      case VoiceStatus.RECORDING:
        return "Click to stop recording";
      case VoiceStatus.UPLOADING:
        return "Processing voice...";
      default:
        return "Click to start voice input";
    }
  }, [status, inputMode]);

  /**
   * @name getContainerClassName 获取容器的 CSS 类名
   * @returns 根据当前状态返回不同的类名
   */
  const getContainerClassName = useCallback(() => {
    const baseClass = "voice-input";
    const statusClass = `voice-input--${status}`;
    const noMicClass =
      inputMode === InputMode.NO_MICROPHONE ? "voice-input--no-mic" : "";
    const disabledClass = disabled ? "voice-input--disabled" : "";
    return [baseClass, statusClass, noMicClass, disabledClass]
      .filter(Boolean)
      .join(" ");
  }, [status, inputMode, disabled]);

  return (
    <>
      {/* 录音中和上传中隐藏 Voice 按钮，因为 VoiceWaveform 组件中有停止按钮/loading */}
      {status !== VoiceStatus.RECORDING && status !== VoiceStatus.UPLOADING && (
        <Tooltip title={getTooltipTitle()}>
          <div
            onClick={onRecording}
            className={getContainerClassName()}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={getTooltipTitle()}
            aria-disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRecording();
              }
            }}
          >
            {/* 麦克风图标 */}
            <img
              src={ICON_VOICE}
              alt="Voice input"
              className="voice-input__icon"
              draggable={false}
            />
          </div>
        </Tooltip>
      )}
    </>
  );
});

// 设置显示名称用于调试
Voice.displayName = "Voice";

export default memo(Voice);
